import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useApi from '../hooks/useApi';

const unwrapApiRows = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const minutesToTimeString = (totalMinutes) => {
  if (Number.isNaN(totalMinutes) || totalMinutes < 0) {
    return '';
  }
  const clamped = Math.min(totalMinutes, 23 * 60 + 59);
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const timeStringToMinutes = (value) => {
  if (!value) {
    return Number.NaN;
  }
  const [hoursStr, minutesStr] = value.split(':');
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return Number.NaN;
  }
  return hours * 60 + minutes;
};

const createEmptySlot = () => ({
  slotLabel: '',
  maxCapacity: 1,
  specialNote: '',
});

const createEmptySession = () => ({
  sessionLabel: '',
  timeSlot: '',
  specialNote: '',
  slots: [createEmptySlot()],
});

const ImportSchedulePage = () => {
  const navigate = useNavigate();
  const { rageRoom } = useApi();

  const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || 'default-location';
  const defaultTimezone = process.env.REACT_APP_DEFAULT_TIMEZONE || 'America/Los_Angeles';

  const [rawRooms, setRawRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState(null);

  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [defaultSlotCapacity, setDefaultSlotCapacity] = useState(1);
  const [replaceExisting, setReplaceExisting] = useState(true);

  const [sessionDurationMinutes, setSessionDurationMinutes] = useState(30);
  const [sessionStartTime, setSessionStartTime] = useState('');
  const [sessionEndTime, setSessionEndTime] = useState('');
  const [peoplePerSession, setPeoplePerSession] = useState(1);
  const [autoBuildError, setAutoBuildError] = useState(null);

  const [sessions, setSessions] = useState([createEmptySession()]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchRooms = async () => {
      if (!rageRoom?.admin?.listRooms) {
        setRoomsError('Admin room service is not available.');
        setRoomsLoading(false);
        return;
      }

      setRoomsLoading(true);
      setRoomsError(null);

      try {
        const response = await rageRoom.admin.listRooms({ locationId });
        if (!isMounted) {
          return;
        }
        setRawRooms(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setRoomsError(error.message || 'Unable to load rage rooms.');
          setRawRooms([]);
        }
      } finally {
        if (isMounted) {
          setRoomsLoading(false);
        }
      }
    };

    fetchRooms();

    return () => {
      isMounted = false;
    };
  }, [locationId]);

  const normalizedRooms = useMemo(
    () =>
      rawRooms
        .map((room) => ({
          id: room.id || room._id || room.roomId || room.slug || '',
          name: room.name || room.displayName || 'Unnamed Room',
          city: room.city || room.location?.city || room.locationName || 'Unknown City',
        }))
        .filter((room) => room.id),
    [rawRooms]
  );

  const selectedRoom = useMemo(
    () => normalizedRooms.find((room) => room.id === selectedRoomId) || null,
    [normalizedRooms, selectedRoomId]
  );

  const sessionsValid = useMemo(
    () =>
      sessions.every((session) => {
        if (!session.sessionLabel.trim() || !session.timeSlot) {
          return false;
        }
        if (!session.slots.length) {
          return false;
        }
        return session.slots.every(
          (slot) => slot.slotLabel.trim() && Number(slot.maxCapacity) > 0
        );
      }),
    [sessions]
  );

  const canSubmit = Boolean(
    selectedRoomId && scheduleDate && timezone.trim() && sessionsValid && !submitLoading
  );

  const resetForm = () => {
    setScheduleDate('');
    setTimezone(defaultTimezone);
    setDefaultSlotCapacity(1);
    setReplaceExisting(true);
    setSessions([createEmptySession()]);
    setSessionDurationMinutes(30);
    setSessionStartTime('');
    setSessionEndTime('');
    setPeoplePerSession(1);
    setAutoBuildError(null);
    setSubmitError(null);
  };

  const handleAddSession = () => {
    setSessions((prev) => [...prev, createEmptySession()]);
  };

  const handleRemoveSession = (index) => {
    if (sessions.length === 1) {
      return;
    }
    setSessions((prev) => prev.filter((_, sessionIndex) => sessionIndex !== index));
  };

  const handleSessionFieldChange = (index, field) => (event) => {
    const value = event.target.value;
    setSessions((prev) =>
      prev.map((session, sessionIndex) =>
        sessionIndex === index ? { ...session, [field]: value } : session
      )
    );
  };

  const handleAddSlot = (sessionIndex) => {
    setSessions((prev) =>
      prev.map((session, idx) =>
        idx === sessionIndex
          ? {
              ...session,
              slots: [...session.slots, createEmptySlot()],
            }
          : session
      )
    );
  };

  const handleRemoveSlot = (sessionIndex, slotIndex) => {
    setSessions((prev) =>
      prev.map((session, idx) => {
        if (idx !== sessionIndex) {
          return session;
        }
        if (session.slots.length === 1) {
          return session;
        }
        return {
          ...session,
          slots: session.slots.filter((_, sIndex) => sIndex !== slotIndex),
        };
      })
    );
  };

  const handleSlotFieldChange = (sessionIndex, slotIndex, field) => (event) => {
    const value = event.target.value;
    setSessions((prev) =>
      prev.map((session, idx) => {
        if (idx !== sessionIndex) {
          return session;
        }
        return {
          ...session,
          slots: session.slots.map((slot, sIndex) =>
            sIndex === slotIndex ? { ...slot, [field]: value } : slot
          ),
        };
      })
    );
  };

  const handleAutoBuildSessions = () => {
    setAutoBuildError(null);
    const duration = Math.max(Number(sessionDurationMinutes) || 0, 0);
    const perSession = Math.max(Number(peoplePerSession) || 0, 0);
    const startMinutes = timeStringToMinutes(sessionStartTime);
    const endMinutes = timeStringToMinutes(sessionEndTime);

    if (!duration || duration < 1) {
      setAutoBuildError('Session duration must be at least 1 minute.');
      return;
    }
    if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes)) {
      setAutoBuildError('Please provide both a valid start time and end time.');
      return;
    }
    if (endMinutes <= startMinutes) {
      setAutoBuildError('End time must be later than the start time.');
      return;
    }
    if (!perSession || perSession < 1) {
      setAutoBuildError('People per session must be at least 1.');
      return;
    }

    const generatedSessions = [];
    let currentStart = startMinutes;
    let sessionCounter = 1;
    const maxSessionsPerDay = Math.ceil((24 * 60) / duration) + 1;
    let iterations = 0;

    while (currentStart <= endMinutes && iterations < maxSessionsPerDay) {
      const sessionLabel = `Session ${sessionCounter}`;
      const slotTime = minutesToTimeString(currentStart);
      const slots = Array.from({ length: perSession }).map((_, index) => ({
        slotLabel: `Smasher ${index + 1}`,
        maxCapacity: 1,
        specialNote: '',
      }));
      generatedSessions.push({
        sessionLabel,
        timeSlot: slotTime,
        specialNote: '',
        slots,
      });
      sessionCounter += 1;
      currentStart += duration;
      iterations += 1;
    }

    if (iterations >= maxSessionsPerDay && currentStart <= endMinutes) {
      setAutoBuildError(
        'The provided window would create too many sessions. Try expanding the duration or narrowing the time range.'
      );
      return;
    }

    if (!generatedSessions.length) {
      setAutoBuildError('Unable to create sessions with the provided window and duration.');
      return;
    }

    setSessions(generatedSessions);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!rageRoom?.admin?.upsertSchedule) {
      setSubmitError('Schedule import service is not available.');
      return;
    }

    if (!canSubmit) {
      setSubmitError('Please complete all required fields before importing.');
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const payload = {
        date: scheduleDate,
        timezone: timezone.trim(),
        defaultSlotCapacity: Number(defaultSlotCapacity) || 1,
        replaceExisting,
        sessions: sessions.map((session, sessionIndex) => ({
          sessionNumber: sessionIndex + 1,
          sessionLabel: session.sessionLabel.trim(),
          timeSlot: session.timeSlot,
          specialNote: session.specialNote.trim(),
          slots: session.slots.map((slot, slotIndex) => ({
            slotLabel: slot.slotLabel.trim() || `Slot ${slotIndex + 1}`,
            maxCapacity: Math.max(Number(slot.maxCapacity) || 1, 1),
            specialNote: slot.specialNote.trim(),
          })),
        })),
      };

      await rageRoom.admin.upsertSchedule({
        locationId,
        roomId: selectedRoomId,
        payload,
      });

      setSubmitSuccess(
        `Schedule imported for ${selectedRoom?.name || 'selected room'} on ${scheduleDate}.`
      );
      resetForm();
    } catch (error) {
      setSubmitError(error.message || 'Unable to import schedule right now.');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Schedule Import
            </Typography>
            <Typography variant="h4" gutterBottom>
              Import Rage Room Schedule
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 620 }}>
              Select a room, choose the target date, and define each 30-minute session with its slots.
              This form mirrors the payload for the admin upsert schedule endpoint so you can manage inventory without touching JSON.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {submitLoading ? 'Importing…' : 'Import Schedule'}
            </Button>
          </Stack>
        </Stack>

        {roomsError && (
          <Alert severity="error" onClose={() => setRoomsError(null)}>
            {roomsError}
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
        {submitSuccess && (
          <Alert severity="success" onClose={() => setSubmitSuccess(null)}>
            {submitSuccess}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">Room & Day</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Rage Room"
                    value={selectedRoomId}
                    onChange={(event) => setSelectedRoomId(event.target.value)}
                    required
                    fullWidth
                    helperText={
                      roomsLoading
                        ? 'Loading rooms…'
                        : 'Choose the room that will receive this schedule.'
                    }
                    disabled={roomsLoading || !normalizedRooms.length}
                  >
                    {roomsLoading && (
                      <MenuItem value="">
                        <CircularProgress size={18} sx={{ mr: 1 }} /> Loading…
                      </MenuItem>
                    )}
                    {!roomsLoading && !normalizedRooms.length && (
                      <MenuItem value="" disabled>
                        No rooms available
                      </MenuItem>
                    )}
                    {normalizedRooms.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name} • {room.city}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="date"
                    label="Schedule Date"
                    value={scheduleDate}
                    onChange={(event) => setScheduleDate(event.target.value)}
                    required
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Timezone"
                    value={timezone}
                    onChange={(event) => setTimezone(event.target.value)}
                    required
                    fullWidth
                    helperText="IANA timezone, e.g., America/Los_Angeles"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="Default Slot Capacity"
                    value={defaultSlotCapacity}
                    onChange={(event) =>
                      setDefaultSlotCapacity(Math.max(Number(event.target.value) || 1, 1))
                    }
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText="Fallback max capacity when not specified per slot."
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={replaceExisting}
                        onChange={(event) => setReplaceExisting(event.target.checked)}
                      />
                    }
                    label="Replace existing schedule"
                  />
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography variant="h6">Auto-Build Sessions</Typography>
                <Typography color="text.secondary">
                  Provide a duration, start time, end time (treated as the last session start), and
                  number of smashers per session to automatically create sequential time slots.
                  Each slot will allow only one person.
                </Typography>
              </Stack>
              {autoBuildError && (
                <Alert severity="warning" onClose={() => setAutoBuildError(null)}>
                  {autoBuildError}
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="time"
                    label="Start Time"
                    value={sessionStartTime}
                    onChange={(event) => setSessionStartTime(event.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="time"
                    label="End Time"
                    value={sessionEndTime}
                    onChange={(event) => setSessionEndTime(event.target.value)}
                    fullWidth
                    helperText="End time is the final session start you want to include."
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="Session Duration (minutes)"
                    value={sessionDurationMinutes}
                    onChange={(event) =>
                      setSessionDurationMinutes(Math.max(Number(event.target.value) || 1, 1))
                    }
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText="Length of each session window."
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="number"
                    label="People per Session"
                    value={peoplePerSession}
                    onChange={(event) =>
                      setPeoplePerSession(Math.max(Number(event.target.value) || 1, 1))
                    }
                    fullWidth
                    inputProps={{ min: 1 }}
                    helperText="Creates that many single-capacity slots."
                  />
                </Grid>
              </Grid>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end">
                <Button variant="contained" onClick={handleAutoBuildSessions}>
                  Build Sessions
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="h6">Sessions & Slots</Typography>
                  <Typography color="text.secondary">
                    Each session represents a 30-minute smash window with one or more slot labels.
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={handleAddSession}>
                  Add Session
                </Button>
              </Stack>

              <Stack spacing={2}>
                {sessions.map((session, sessionIndex) => (
                  <Card key={`session-${sessionIndex}`} variant="outlined">
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={1.5}
                        >
                          <Typography variant="subtitle1">
                            Session {sessionIndex + 1}
                          </Typography>
                          <Button
                            color="error"
                            onClick={() => handleRemoveSession(sessionIndex)}
                            disabled={sessions.length === 1}
                          >
                            Remove Session
                          </Button>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              label="Session Label"
                              value={session.sessionLabel}
                              onChange={handleSessionFieldChange(sessionIndex, 'sessionLabel')}
                              required
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              type="time"
                              label="Time Slot"
                              value={session.timeSlot}
                              onChange={handleSessionFieldChange(sessionIndex, 'timeSlot')}
                              required
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField
                              label="Session Note"
                              value={session.specialNote}
                              onChange={handleSessionFieldChange(sessionIndex, 'specialNote')}
                              fullWidth
                              placeholder="Optional note for this session."
                            />
                          </Grid>
                        </Grid>

                        <Divider />

                        <Stack spacing={2}>
                          <Typography variant="body2" fontWeight={600}>
                            Slots
                          </Typography>
                          {session.slots.map((slot, slotIndex) => (
                            <Card key={`slot-${sessionIndex}-${slotIndex}`} variant="outlined">
                              <CardContent>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <TextField
                                      label="Slot Label"
                                      value={slot.slotLabel}
                                      onChange={handleSlotFieldChange(
                                        sessionIndex,
                                        slotIndex,
                                        'slotLabel'
                                      )}
                                      required
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={3}>
                                    <TextField
                                      type="number"
                                      label="Max Capacity"
                                      value={slot.maxCapacity}
                                      onChange={handleSlotFieldChange(
                                        sessionIndex,
                                        slotIndex,
                                        'maxCapacity'
                                      )}
                                      required
                                      fullWidth
                                      inputProps={{ min: 1 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={5}>
                                    <TextField
                                      label="Special Note"
                                      value={slot.specialNote}
                                      onChange={handleSlotFieldChange(
                                        sessionIndex,
                                        slotIndex,
                                        'specialNote'
                                      )}
                                      fullWidth
                                      placeholder="Optional slot note."
                                    />
                                  </Grid>
                                </Grid>
                                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                                  <Button
                                    color="error"
                                    size="small"
                                    onClick={() => handleRemoveSlot(sessionIndex, slotIndex)}
                                    disabled={session.slots.length === 1}
                                  >
                                    Remove Slot
                                  </Button>
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                          <Button
                            variant="text"
                            onClick={() => handleAddSlot(sessionIndex)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Add Slot
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={2}>
          <Button variant="text" color="inherit" onClick={resetForm}>
            Reset Form
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {submitLoading ? 'Importing…' : 'Import Schedule'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ImportSchedulePage;